import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SavedLesson } from '@/mocks/lessons';
import Pressable from '@/components/base/Pressable';

interface LessonResourcesProps {
  lesson: SavedLesson;
  onNavigateTab: (tabId: string) => void;
}

const resourceCards = [
  { id: 'flashcards', label: 'Cue Cards', icon: 'ri-stack-line', countField: 'flashcards', color: 'text-emerald-400', colorLight: 'text-emerald-600', bg: 'bg-emerald-500/[0.06]', bgLight: 'bg-emerald-50', border: 'border-emerald-500/10', borderLight: 'border-emerald-200/60', tabId: 'flashcards' },
  { id: 'quiz', label: 'Quiz', icon: 'ri-questionnaire-line', countField: 'quiz', color: 'text-amber-400', colorLight: 'text-amber-600', bg: 'bg-amber-500/[0.06]', bgLight: 'bg-amber-50', border: 'border-amber-500/10', borderLight: 'border-amber-200/60', tabId: 'quiz' },
  { id: 'summary', label: 'Notes', icon: 'ri-file-list-line', countField: 'summary', color: 'text-sky-400', colorLight: 'text-sky-600', bg: 'bg-sky-500/[0.06]', bgLight: 'bg-sky-50', border: 'border-sky-500/10', borderLight: 'border-sky-200/60', tabId: 'summary' },
  { id: 'teach', label: 'Explain', icon: 'ri-refresh-line', countField: 'teachDifferently', color: 'text-rose-400', colorLight: 'text-rose-600', bg: 'bg-rose-500/[0.06]', bgLight: 'bg-rose-50', border: 'border-rose-500/10', borderLight: 'border-rose-200/60', tabId: 'teach' },
  { id: 'similar', label: 'Similar', icon: 'ri-magic-line', countField: 'similarLessons', color: 'text-violet-400', colorLight: 'text-violet-600', bg: 'bg-violet-500/[0.06]', bgLight: 'bg-violet-50', border: 'border-violet-500/10', borderLight: 'border-violet-200/60', tabId: 'similar' },
  { id: 'keyconcepts', label: 'Key Concepts', icon: 'ri-key-line', countField: 'keyConcepts', color: 'text-teal-400', colorLight: 'text-teal-600', bg: 'bg-teal-500/[0.06]', bgLight: 'bg-teal-50', border: 'border-teal-500/10', borderLight: 'border-teal-200/60', tabId: 'summary' },
];

export default function LessonResources({ lesson, onNavigateTab }: LessonResourcesProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const getCount = (field: string) => {
    switch (field) {
      case 'flashcards': return lesson.flashcards.length;
      case 'quiz': return lesson.quiz.length;
      case 'summary': return lesson.summary.sections.length;
      case 'teachDifferently': return lesson.teachDifferently.length;
      case 'similarLessons': return lesson.similarLessons.length;
      case 'keyConcepts': return lesson.keyConcepts.length;
      default: return 0;
    }
  };

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/25' : 'text-black/25',
    iconBg: isDark ? 'bg-white/[0.06] border-white/[0.06]' : 'bg-black/[0.04] border-black/[0.06]',
    hoverBorder: isDark ? 'border-white/[0.08]' : 'border-black/[0.08]',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 py-4"
    >
      <div className="grid grid-cols-3 gap-2">
        {resourceCards.map((card, i) => {
          const count = getCount(card.countField);
          const isHovered = hoveredCard === card.id;

          return (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + i * 0.05, duration: 0.3 }}
            >
              <Pressable
                onClick={() => onNavigateTab(card.tabId)}
                className={`relative w-full p-3 rounded-2xl border ${isDark ? card.border : card.borderLight} ${isDark ? card.bg : card.bgLight} transition-all duration-200`}
                scale={0.95}
                onHoverStart={() => setHoveredCard(card.id)}
                onHoverEnd={() => setHoveredCard(null)}
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <div className={`w-9 h-9 rounded-xl ${t.iconBg} border flex items-center justify-center`}>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${card.icon} ${isDark ? card.color : card.colorLight} text-sm`} />
                    </div>
                  </div>
                  <div>
                    <p className={`${t.textPrimary} text-xs font-semibold`}>{card.label}</p>
                    <p className={`${t.textMuted} text-[10px] mt-0.5`}>{count} {count === 1 ? 'item' : 'items'}</p>
                  </div>
                </div>

                {/* Hover glow */}
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      className={`absolute inset-0 rounded-2xl border ${t.hoverBorder}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    />
                  )}
                </AnimatePresence>
              </Pressable>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}